import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LucideIcon, MapPin, Search, Star, X } from 'lucide-react';
import appDataService, { Club, Pub, Shop } from '../../services/appDataService';

const ACCENT = '#3fced1';
const BATCH = 24;

export type ListingItem = Club | Pub | Shop;

export interface ListingFilterDef {
  key: string;
  label: string;
  icon?: LucideIcon;
  test: (item: ListingItem) => boolean;
}

export interface ListingBadge {
  key: string;
  label: string;
  icon?: LucideIcon;
}

export interface ListingExplorerProps {
  items: ListingItem[];
  loading: boolean;
  title: string;
  subtitle?: string;
  searchPlaceholder: string;
  emptyIcon: LucideIcon;
  emptyLabel: string;
  /** Basis-Pfad für Detail-Links, z. B. "/spielstaette" */
  routeBase: string;
  /** Icon für leere Bild-Platzhalter & Karten-Fallback */
  fallbackIcon: LucideIcon;
  filters?: ListingFilterDef[];
  badges?: (item: ListingItem) => ListingBadge[];
  /** SEO */
  seoTitle: string;
  seoDescription: string;
}

const idOf = (item: ListingItem): string => item.id || item._id || '';
const slugOf = (item: ListingItem): string => item.slug || '';

/**
 * Wiederverwendbarer, responsiver Listen-Explorer für Vereine, Spielstätten
 * und Shops – Optik/Verhalten orientiert an der Flutter-App:
 * Suche (Name/Ort), Filter-Chips, Sponsored-zuerst → mit-Bild-zuerst, "Mehr laden".
 */
const ListingExplorer: React.FC<ListingExplorerProps> = ({
  items,
  loading,
  title,
  subtitle,
  searchPlaceholder,
  emptyIcon: EmptyIcon,
  emptyLabel,
  routeBase,
  fallbackIcon,
  filters = [],
  badges,
  seoTitle,
  seoDescription,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(BATCH);

  const toggleFilter = (key: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setVisibleCount(BATCH);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = items.filter((item) => item && (item.id || item._id));

    if (term) {
      list = list.filter((item) => {
        const name = appDataService.formatName(item).toLowerCase();
        const address = appDataService.formatAddress(item.address).toLowerCase();
        return name.includes(term) || address.includes(term);
      });
    }

    if (activeFilters.size > 0) {
      list = list.filter((item) =>
        filters.every((f) => (activeFilters.has(f.key) ? f.test(item) : true)),
      );
    }

    // Sponsored zuerst, dann Einträge mit Bild – ansonsten Backend-Reihenfolge (Qualität)
    return [...list].sort((a, b) => {
      const rank = (x: ListingItem) =>
        (x.sponsored ? 0 : 2) + (appDataService.getBannerUrl(x) || appDataService.getLogoUrl(x as Club) ? 0 : 1);
      return rank(a) - rank(b);
    });
  }, [items, search, activeFilters, filters]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <div className="min-h-screen bg-zinc-950">
        {/* Hero-Header */}
        <div
          className="border-b border-zinc-900"
          style={{
            background:
              'radial-gradient(60% 100% at 15% 0%, rgba(63,206,209,0.12), transparent 60%)',
          }}
        >
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <h1 className="text-3xl font-extrabold text-white sm:text-4xl">{title}</h1>
            {subtitle && <p className="mt-2 text-gray-400">{subtitle}</p>}

            {/* Suche */}
            <div className="relative mt-6 max-w-xl">
              <Search
                size={20}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setVisibleCount(BATCH);
                }}
                placeholder={searchPlaceholder}
                className="h-12 w-full rounded-2xl border border-zinc-800 bg-zinc-900 pl-12 pr-10 text-[15px] text-white outline-none transition-colors placeholder:text-gray-500 focus:border-[#3fced1]/60"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Suche löschen"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Filter-Chips */}
            {filters.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {filters.map((f) => {
                  const active = activeFilters.has(f.key);
                  const Icon = f.icon;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => toggleFilter(f.key)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                        active
                          ? 'border-transparent text-black'
                          : 'border-zinc-700 text-gray-300 hover:border-zinc-500'
                      }`}
                      style={active ? { backgroundColor: ACCENT } : undefined}
                    >
                      {Icon && <Icon size={15} />}
                      {f.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Inhalt */}
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          {!loading && (
            <p className="mb-6 text-sm text-gray-500">
              {filtered.length} {filtered.length === 1 ? 'Eintrag' : 'Einträge'}
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[300px] animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <EmptyIcon size={56} className="mb-4 text-zinc-700" />
              <h3 className="mb-1 text-xl font-semibold text-white">{emptyLabel}</h3>
              <p className="text-gray-500">Versuche andere Suchbegriffe oder Filter.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((item) => (
                  <ListingCard
                    key={`${item.slug || idOf(item)}`}
                    item={item}
                    fallbackIcon={fallbackIcon}
                    badges={badges?.(item) ?? []}
                    onClick={() => {
                      const slug = slugOf(item);
                      if (slug) navigate(`${routeBase}/${slug}`);
                    }}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + BATCH)}
                    className="rounded-xl border border-zinc-700 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-zinc-500 hover:bg-zinc-900"
                  >
                    Mehr laden ({filtered.length - visibleCount})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Karte
// ---------------------------------------------------------------------------

const ListingCard: React.FC<{
  item: ListingItem;
  fallbackIcon: LucideIcon;
  badges: ListingBadge[];
  onClick: () => void;
}> = ({ item, fallbackIcon: FallbackIcon, badges, onClick }) => {
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const banner = appDataService.getBannerUrl(item);
  const logo = appDataService.getLogoUrl(item as Club);
  const name = appDataService.formatName(item);
  const address = appDataService.formatAddress(item.address);
  const showBanner = banner && !bannerError;
  const showLogo = logo && !logoError;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!item.slug}
      className="group flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-all hover:-translate-y-1 hover:border-zinc-700 disabled:cursor-default"
    >
      {/* Banner */}
      <div className="relative h-44 w-full shrink-0 bg-zinc-800">
        {showBanner ? (
          <img
            src={banner as string}
            alt={name}
            onError={() => setBannerError(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <FallbackIcon size={44} className="text-zinc-600" />
          </div>
        )}

        {/* Sponsored-Badge */}
        {item.sponsored && (
          <span
            className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-bold text-black"
            style={{ backgroundColor: ACCENT }}
          >
            <Star size={12} className="fill-black" /> Empfohlen
          </span>
        )}

        {/* Logo-Overlay */}
        {showLogo && (
          <div className="absolute -bottom-6 left-4 h-14 w-14 overflow-hidden rounded-xl border-2 border-zinc-900 bg-white shadow-lg">
            <img
              src={logo as string}
              alt=""
              onError={() => setLogoError(true)}
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Inhalt */}
      <div className={`flex flex-1 flex-col p-4 ${showLogo ? 'pt-8' : ''}`}>
        <h3 className="line-clamp-1 text-lg font-bold text-white">{name}</h3>

        <div className="mt-1.5 flex items-start gap-1.5 text-sm text-gray-400">
          <MapPin size={15} className="mt-0.5 shrink-0" />
          <span className="line-clamp-2">{address}</span>
        </div>

        {badges.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-4">
            {badges.map((b) => {
              const Icon = b.icon;
              return (
                <span
                  key={b.key}
                  className="inline-flex items-center gap-1 rounded-md bg-zinc-800 px-2 py-1 text-[11px] font-medium text-gray-300"
                >
                  {Icon && <Icon size={12} style={{ color: ACCENT }} />}
                  {b.label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </button>
  );
};

export default ListingExplorer;
